<template>
    <div
        v-if="itensValidos.length"
        ref="wrapper"
        class="banner-carousel"
        :class="`banner-carousel--${variant}`"
        :style="bannerHeight ? {height: bannerHeight + 'px'} : null"
    >
        <v-carousel
            v-if="bannerHeight"
            height="100%"
            :cycle="autoplay && itensValidos.length > 1"
            :interval="intervaloMs"
            :show-arrows="itensValidos.length > 1 && variant === 'mobile'"
            :hide-delimiters="itensValidos.length < 2"
            hide-delimiter-background
        >
            <v-carousel-item
                v-for="(item, i) in itensValidos"
                :key="i"
            >
                <div class="banner-slide">
                    <div
                        v-if="formatoOriginal"
                        class="banner-slide__fundo"
                        :style="{backgroundImage: `url('${getUrl(item)}')`}"
                    />

                    <v-img
                        :src="getUrl(item)"
                        height="100%"
                        :contain="formatoOriginal"
                        class="banner-slide__img"
                    />

                    <div
                        v-if="item.titulo || item.subtitulo"
                        class="banner-slide__overlay"
                    >
                        <div class="banner-slide__text">
                            <div v-if="item.titulo" class="banner-slide__title">
                                {{ item.titulo }}
                            </div>
                            <div v-if="item.subtitulo" class="banner-slide__subtitle">
                                {{ item.subtitulo }}
                            </div>
                        </div>
                    </div>
                </div>
            </v-carousel-item>
        </v-carousel>
    </div>
</template>

<script>
const RATIOS = {
    quadrado: 1,
    paisagem: 9 / 16,
    retrato: 5 / 4,
};

const CAP = {
    mobile: 0.45,
    tablet: 0.62,
};

const CAP_ORIGINAL = {
    mobile: 0.7,
    tablet: 0.7,
};

export default {
    name: 'BannerCarousel',

    props: {
        itens: {
            type: Array,
            default: () => [],
        },
        formato: {
            type: String,
            default: 'paisagem',
        },
        variant: {
            type: String,
            default: 'mobile', // 'mobile' | 'tablet'
        },
        autoplay: {
            type: Boolean,
            default: true,
        },
        intervalo: {
            type: Number,
            default: 6,
        },
    },

    data: () => ({
        containerW: 0,
        winH: 0,
        ratioNatural: 0,
    }),

    computed: {
        intervaloMs() {
            return Math.max(3, this.intervalo || 6) * 1000;
        },

        formatoOriginal() {
            return this.formato === 'original';
        },

        itensValidos() {
            return (this.itens || []).filter(item => item && item.url);
        },

        ratio() {
            return this.formatoOriginal
                ? this.ratioNatural
                : (RATIOS[this.formato] || RATIOS.paisagem);
        },

        bannerHeight() {
            if (!this.containerW || !this.ratio) return 0;

            const caps = this.formatoOriginal ? CAP_ORIGINAL : CAP;
            const capFactor = caps[this.variant] || caps.mobile;
            const teto = this.winH ? this.winH * capFactor : Infinity;

            return Math.round(Math.min(this.containerW * this.ratio, teto));
        },
    },

    watch: {
        formato: {
            immediate: true,
            handler: 'medirPrimeiraImagem',
        },
        itensValidos: 'medirPrimeiraImagem',
    },

    mounted() {
        this._onResize = () => {
            this.containerW = this.$refs.wrapper ? this.$refs.wrapper.clientWidth : 0;
            this.winH = window.innerHeight;
        };
        window.addEventListener('resize', this._onResize);
        window.addEventListener('orientationchange', this._onResize);
        this.$nextTick(this._onResize);
        // O WebView reporta viewport errado no primeiro frame; medir de novo depois.
        this._resizeTimer = setTimeout(this._onResize, 600);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('orientationchange', this._onResize);
        clearTimeout(this._resizeTimer);
    },

    methods: {
        medirPrimeiraImagem() {
            if (!this.formatoOriginal) {
                return;
            }

            const primeiro = this.itensValidos[0];
            if (!primeiro) {
                return;
            }

            const url = this.getUrl(primeiro);
            if (url === this._urlMedida) {
                return;
            }
            this._urlMedida = url;
            this.ratioNatural = 0;

            const img = new Image();

            img.onload = () => {
                // A imagem pode ter mudado enquanto esta carregava.
                if (this._urlMedida !== url) return;
                this.ratioNatural = img.naturalWidth
                    ? img.naturalHeight / img.naturalWidth
                    : RATIOS.paisagem;
            };

            img.onerror = () => {
                if (this._urlMedida !== url) return;
                this.ratioNatural = RATIOS.paisagem;
            };

            img.src = url;
        },

        getUrl(item) {
            if (!item || !item.url) return '';
            return item.tmp
                ? `${this.$urlAPI}/files/tmp/${item.url}`
                : `${this.$urlAPI}/storage/${item.url}`;
        },
    },
};
</script>

<style scoped>
.banner-carousel {
    box-sizing: border-box;
    border-radius: 10px;
    overflow: hidden;
}

.banner-carousel--tablet {
    border-radius: 20px;
}

.banner-slide {
    position: relative;
    height: 100%;
    width: 100%;
}

.banner-slide__fundo {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    background-position: center;
    background-size: cover;
    filter: blur(18px) brightness(0.75);
    transform: scale(1.1);
}

.banner-slide__img {
    position: relative;
    z-index: 1;
}

.banner-slide__overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    padding: 28px 32px;
    text-align: left;
    color: #fff;
    background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.78) 0%,
        rgba(0, 0, 0, 0.45) 45%,
        rgba(0, 0, 0, 0) 100%
    );
}

.banner-slide__text {
    max-width: 72%;
}

.banner-slide__title {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.1;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);
}

.banner-slide__subtitle {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.35;
    margin-top: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    opacity: 0.95;
    text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
}

.banner-carousel--tablet .banner-slide__overlay {
    padding: 36px 44px;
}

.banner-carousel--tablet .banner-slide__text {
    max-width: 65%;
}

.banner-carousel--tablet .banner-slide__title {
    font-size: 52px;
}

.banner-carousel--tablet .banner-slide__subtitle {
    font-size: 22px;
    margin-top: 12px;
}
</style>
